/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable jsx-a11y/img-redundant-alt */
import React from 'react';
import { Typography, Card } from '@mui/material';
import BackButton from '../BackButton';
import BeyondAdasImage from "../../../../../../assets/images/ArticleImages/BeyondAdas.webp";
import RiseofDriver from "../../../../../../assets/images/ArticleImages/Rise-of-Driver-Monitoring-with-ADAS--1024x683.webp";

// Define a type for the content blocks
type ContentBlock =
    | { type: 'heading'; value: string; variant?: 'h4' | 'h5' | 'h6' | 'subtitle2'; style?: React.CSSProperties }
    | { type: 'paragraph'; value: string; style?: React.CSSProperties }
    | { type: 'image'; src: string; alt?: string; style?: React.CSSProperties };

const BeyondADAS: React.FC = () => {
    // Page header information
    const title = "Beyond ADAS: The Evolving Landscape of Driver Monitoring in Smart Mobility";
    const headerImageSrc = BeyondAdasImage;

    // Define the content of your article as a list of blocks.
    const contentBlocks: ContentBlock[] = [
        {
            type: 'paragraph',
            value: "In the ever-evolving realm of smart mobility, safety remains a paramount concern. Advanced Driver Assistance Systems (ADAS) have played a pivotal role in enhancing road safety by employing technologies like lane-keeping assistance, collision avoidance, and adaptive cruise control. However, the landscape is evolving, and the integration of Driver Monitoring Systems (DMS) is becoming increasingly crucial, especially for fleet operators. This blog explores the rise of DMS, its applications, advancements, and future outlook in shaping the future of smart mobility for both individual drivers and fleet operators alike.",
        },
        {
            type: 'heading',
            value: "The Rise of Driver Monitoring Systems",
            variant: 'h6',
            style: { marginTop: '16px', marginBottom: '8px' },
        },
        {
            type: 'paragraph',
            value: "As vehicles become more connected and autonomous, the need to monitor driver behavior becomes imperative. Driver Monitoring Systems (DMS) are becoming increasingly prevalent in modern vehicles, offering a proactive approach to enhancing safety and comfort. These systems employ various technologies to detect and mitigate potential risks associated with distracted, fatigued, or impaired driving.",
        },
        {
            type: 'paragraph',
            value: "Types of DMS technologies include:",
        },
        {
            type: 'paragraph',
            value: (
                <>
                    •<strong> In-Cabin Cameras:</strong> These cameras are strategically placed within the vehicle’s cabin to monitor the driver’s face, eye movements, and head position, providing valuable insights into their attentiveness and alertness levels.
                </>
            ) as unknown as string,
        },
        {
            type: 'paragraph',
            value: (
                <>
                    •<strong> Steering Pattern Analysis:</strong> By analyzing the driver’s steering inputs, these systems can identify patterns that may indicate distraction, drowsiness, or impaired driving.
                </>
            ) as unknown as string,
        },
        {
            type: 'paragraph',
            value: (
                <>
                    •<strong> Biometric Sensors:</strong> Sensors integrated into the steering wheel, seat, or other surfaces can monitor the driver’s heart rate, respiration, and other vital signs, helping to detect fatigue or impairment.
                </>
            ) as unknown as string,
        },
        {
            type: 'heading',
            value: "Applications of DMS in Smart Mobility",
            variant: 'h6',
            style: { marginTop: '16px', marginBottom: '8px' },
        },
        {
            type: 'paragraph',
            value: "The applications of DMS in smart mobility extend beyond safety enhancement, encompassing both safety and personalization aspects:",
        },
        {
            type: 'paragraph',
            value: "• Safety Enhancement",
        },
        {
            type: 'paragraph',
            value: "• Fatigue Detection: DMS can identify signs of driver drowsiness, such as yawning, heavy eyelids, and erratic steering, and provide timely alerts or initiate safety measures.",
        },
        {
            type: 'paragraph',
            value: "• Distraction Monitoring: By monitoring eye movements and head position, DMS can detect when a driver’s attention is diverted from the road, enabling appropriate interventions.",
        },
        {
            type: 'paragraph',
            value: "• Impaired Driving Detection: Biometric sensors and behavior analysis can help identify signs of impaired driving, potentially preventing accidents caused by substance abuse or medical conditions.",
        },
        {
            type: 'paragraph',
            value: "• Personalization and Comfort",
        },
        {
            type: 'paragraph',
            value: "• Adaptive Climate Control: DMS data can be used to adjust the vehicle’s climate control system based on the driver’s physiological state, ensuring optimal comfort.",
        },
        {
            type: 'paragraph',
            value: "• Customized Infotainment: By recognizing the driver’s preferences and habits, DMS can tailor the infotainment system for a personalized experience, reducing distractions and enhancing convenience.",
        },
        {
            type: 'heading',
            value: "Complementing ADAS and Growing Demand",
            variant: 'h6',
            style: { marginTop: '16px', marginBottom: '8px' },
        },
        {
              type: 'image',
              src: RiseofDriver,
              alt: "Connected vehicles schematic",
              style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
            },
            {
                type: 'paragraph',
                value: "While ADAS has significantly improved road safety, it primarily focuses on environmental factors and vehicle dynamics. Driver monitoring systems complement ADAS by addressing the human factor, which accounts for a significant portion of accidents. As the demand for enhanced safety and personalized driving experiences grows, the automotive industry is increasingly recognizing the value of integrating DMS into their vehicle lineups.",
            },
            {
                type: 'heading',
                value: "Future Outlook",
                variant: 'h6',
                style: { marginTop: '16px', marginBottom: '8px' },
            },
            {
                type: 'paragraph',
                value: "As technology continues to advance, we can expect to see further integration of DMS with other smart mobility solutions, such as autonomous vehicles and ride-sharing services. Additionally, the use of edge computing, 5G, and advanced sensor technologies will enable real-time data processing and analysis, leading to more accurate and responsive driver monitoring systems.",
            },
            {
                type: 'paragraph',
                value: "The impact of driver monitoring systems on the automotive industry, transportation safety, and mobility services cannot be overstated. By addressing the human factor in driving, DMS has the potential to significantly reduce accidents, enhance the user experience, and pave the way for a safer and smarter mobility future. However, the development and adoption of driver monitoring systems must prioritize ethical practices, ensuring privacy, transparency, and user consent.",
            },
            {
                type: 'heading',
                value: "Conclusion: Revolutionizing Smart Mobility with Driver Monitoring",
                variant: 'h6',
                style: { marginTop: '16px', marginBottom: '8px' },
            },
            {
                type: 'paragraph',
                value: "By combining cutting-edge technologies like computer vision, AI, and biometric sensors, DMS offers a comprehensive approach to addressing the human factor in driving. From enhancing safety through fatigue detection and distraction monitoring to personalizing the driving experience through adaptive climate control and customized infotainment, DMS is redefining the future of smart mobility.",
            },
    ];

    // Function to render each content block based on its type.
    const renderContentBlock = (block: ContentBlock, index: number) => {
        switch (block.type) {
            case 'heading':
                return (
                    <Typography
                        key={index}
                        variant={block.variant || 'h4'}
                        style={{ fontWeight: 600, ...block.style }}
                    >
                        {block.value}
                    </Typography>
                );
            case 'paragraph':
                return (
                    <Typography
                        key={index}
                        variant="body1"
                        style={{
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '24px',
                            // color: 'gray',
                            ...block.style,
                        }}
                    >
                        {block.value}
                    </Typography>
                );
            case 'image':
                return (
                    <Card
                        key={index}
                        style={{
                            overflow: 'hidden',
                            borderRadius: 12,
                            ...block.style,
                        }}
                    >
                        <img
                            src={block.src}
                            alt={block.alt || 'Article image'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Card>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col w-full">
            <BackButton />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
                {/* Header Section */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '766px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                    }}
                >
                    {/* Title & Tag Section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Typography variant="h4" style={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '8px',
                            }}
                        >
                        </div>
                    </div>

                    {/* Header Image */}
                    <Card
                        style={{
                            width: '100%',
                            height: 384,
                            borderRadius: 12,
                            overflow: 'hidden',
                        }}
                    >
                        <img
                            src={headerImageSrc}
                            alt="Article header image"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Card>
                </div>

                {/* Content Section */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '766px',
                        padding: '16px 0',
                    }}
                >
                    {contentBlocks.map((block, index) => renderContentBlock(block, index))}
                </div>
            </div>
        </div>
    );
};

export default BeyondADAS;


